import { AuthenticatedTemplate, useAccount, UnauthenticatedTemplate, useMsal } from '@azure/msal-react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { loginRequest } from '../../../authConfig';
import React, { useEffect, useRef, useState } from 'react';
import { Icon } from '../icons/Icons';
import styles from './NavigationBar.module.css';
import { Button } from '../buttons';
// import NewCanvassModal from '../../canvasses/NewCanvassModal';
import { ensureFirstCanvassCreatedCached, getAllClients, updateFirstCanvassCreated } from '../../../utils/clients';
import { getIsFirstCanvassCreated, setIsFirstCanvassCreated, getClient } from '../../../utils/globalState';
import { logger } from '../../../utils/logger';

// Non-authenticated navigation component
const NonAuthNavigationBar = () => {
    const { instance } = useMsal();
    const location = useLocation();
    const [menuOpen, setMenuOpen] = useState(false);
    const [canEndInterview, setCanEndInterview] = useState(false);
    const [isDisabled, setIsDisabled] = useState(false);
    const [isInterviewPage, setIsInterviewPage] = useState(
        location.pathname.startsWith('/interview/') || location.pathname.startsWith('/voice/')
    );

    // Listen for interview state changes
    useEffect(() => {
        const handleInterviewStateChanged = (event) => {
            const { canEndInterview: canEnd, disabled, userReplyCount } = event.detail;
            console.log('[NavigationBar] Interview state changed:', { canEnd, disabled, userReplyCount });
            setCanEndInterview(canEnd);
            setIsDisabled(disabled);
            
            // Add/remove class from root to hide scrollbar
            const rootElement = document.getElementById('root');
            if (rootElement) {
                if (canEnd && isInterviewPage) {
                    rootElement.classList.add('interview-page-active');
                } else {
                    rootElement.classList.remove('interview-page-active');
                }
            }
        };

        const handleInterviewPageDetected = (event) => {
            const { isInterviewPage } = event.detail;
            setIsInterviewPage(isInterviewPage);
            
            // Add/remove class from root when page detection changes
            const rootElement = document.getElementById('root');
            if (rootElement) {
                if (!isInterviewPage) {
                    rootElement.classList.remove('interview-page-active');
                }
            }
        };

        window.addEventListener('interviewStateChanged', handleInterviewStateChanged);
        window.addEventListener('interviewPageDetected', handleInterviewPageDetected);
        
        return () => {
            window.removeEventListener('interviewStateChanged', handleInterviewStateChanged);
            window.removeEventListener('interviewPageDetected', handleInterviewPageDetected);
            
            // Cleanup class on unmount
            const rootElement = document.getElementById('root');
            if (rootElement) {
                rootElement.classList.remove('interview-page-active');
            }
        };
    }, [isInterviewPage]);

    const handleLoginRedirect = () => {
        instance.loginRedirect(loginRequest).catch((error) => logger.log(error));
    };

    return (
        <nav className={styles.navBar}>
            <a href="/" className={styles.navLogoContainer}>
                <Icon name="augustsays/logo" color="var(--nav-logo-color)" height="32" width="61"/>
            </a>
            
            {isInterviewPage && canEndInterview ? (
                <div className={`${styles.navActionContainer} ${styles.desktopActions}`}>
                    <Button
                        type="button"
                        variant="primary"
                        size="sm"
                        disabled={isDisabled}
                        onClick={() => {
                            // Dispatch custom event to notify GameAssistantChat
                            window.dispatchEvent(new CustomEvent('endInterviewRequested'));
                        }}
                    >
                        End Interview
                    </Button>
                </div>
            ) : (
                <>
                <div className={styles.navMenuContainer}>
                </div>
                <div className={`${styles.navActionContainer} ${styles.desktopActions}`}>
                    <Link to="/#newsletter" style={{ textDecoration: 'none', marginRight: 12 }}>
                        <Button size='lg' variant="secondary">
                            Sign up
                        </Button>
                    </Link>
                    <Button size='lg' variant="primary" onClick={handleLoginRedirect}>
                        Sign In
                    </Button>
                </div>
                <div className={`${styles.navActionContainer} ${styles.mobileActions} ${styles.rightDock}`}>
                    <button
                        type="button"
                        className={styles.hamburger}
                        aria-haspopup="menu"
                        aria-expanded={menuOpen ? 'true' : 'false'}
                        aria-controls="non-auth-mobile-menu"
                        onClick={() => setMenuOpen(v => !v)}
                    >
                        <span className={styles.hamburgerBar} />
                        <span className={styles.hamburgerBar} />
                        <span className={styles.hamburgerBar} />
                    </button>
                    {menuOpen && (
                        <div id="non-auth-mobile-menu" role="menu" className={styles.mobileMenu}>
                            <Link role="menuitem" to="/#newsletter" className={styles.mobileMenuItem} onClick={() => setMenuOpen(false)}>
                                Sign up
                            </Link>
                            <button role="menuitem" type="button" className={styles.mobileMenuItem} onClick={() => { setMenuOpen(false); handleLoginRedirect(); }}>
                                Sign In
                            </button>
                        </div>
                    )}
                </div>
                </>
            )}
        </nav>
    );
};

// Authenticated navigation component (uses MSAL hooks)
const AuthNavigationBar = () => {
    const { instance } = useMsal();
    var account = useAccount(instance.getActiveAccount());
    const location = useLocation();
    const navigate = useNavigate();
    const [clientReady, setClientReady] = useState(false);
    const [authMenuOpen, setAuthMenuOpen] = useState(false);
    const [canEndInterview, setCanEndInterview] = useState(false);
    const [isDisabled, setIsDisabled] = useState(false);
    
    const isInterview = location.pathname.startsWith('/interview/') || location.pathname.startsWith('/voice/');
    const shouldHideNavItems = isInterview;

    // Listen for interview state changes
    useEffect(() => {
        const handleInterviewStateChanged = (event) => {
            const { canEndInterview: canEnd, disabled } = event.detail;
            setCanEndInterview(canEnd);
            setIsDisabled(disabled);
        };

        window.addEventListener('interviewStateChanged', handleInterviewStateChanged);
        
        return () => {
            window.removeEventListener('interviewStateChanged', handleInterviewStateChanged);
        };
    }, []);

    // Set global variables as fallback for now
    window.MSAccountId = "";
    window.MSAccountName = "";
    window.MSAccountEmail = "";
    
    if (account && account.localAccountId) {
        window.MSAccountId = account.localAccountId;
        window.MSAccountName = account.name || 'Unknown User';
        window.MSAccountEmail = account.username || ''; // Email is in username field
    }

    // Wait for client to be fully available (mirrors App client gating)
    useEffect(() => {
        let cancelled = false;
        const checkReady = () => {
            if (!account) { if (!cancelled) setClientReady(true); return; }
            const c = getClient();
            if (c && !c.early) {
                if (!cancelled) setClientReady(true);
            }
        };
        // immediate check and then short polling to avoid race on first mount
        checkReady();
        const id = setInterval(checkReady, 100);
        return () => { cancelled = true; clearInterval(id); };
    }, [account]);

    const handleLoginRedirect = () => {
        instance.loginRedirect(loginRequest).catch((error) => logger.log(error));
    };

    // Initialize global first-canvass flag once per session/account
    useEffect(() => {
        const initFirstCanvassFlag = async () => {
            try {
                const already = getIsFirstCanvassCreated();
                if (typeof already !== 'undefined') return;
                const oid = instance.getActiveAccount()?.idTokenClaims?.oid || account?.localAccountId;
                if (!oid) return;
                const { isFirstCanvassCreated } = await ensureFirstCanvassCreatedCached({ clientId: oid });
                setIsFirstCanvassCreated(isFirstCanvassCreated);
            } catch {}
        };
        if (account) {
            initFirstCanvassFlag();
        }
    }, [account, instance]);

    // Client sync moved to RequireAuth in AppRoutes.jsx


    const handleLogoutRedirect = () => {
        instance.logoutRedirect().catch((error) => logger.log(error));
    };

    const [showNewCanvassModal, setShowNewCanvassModal] = useState(false);
    const [showTutorialModal, setShowTutorialModal] = useState(false);
    const [canvassTitle, setCanvassTitle] = useState('');
    const [canvassBrief, setCanvassBrief] = useState('');
    const [adminClients, setAdminClients] = useState([]);
    const [adminClientsLoading, setAdminClientsLoading] = useState(false);
    const [adminClientsError, setAdminClientsError] = useState('');
    const [selectedAdminClientId, setSelectedAdminClientId] = useState('');
    const [formError, setFormError] = useState('');
    const [showProfileMenu, setShowProfileMenu] = useState(false);
    const [highlightNewCanvass, setHighlightNewCanvass] = useState(false);
    const [highlightCanvasses, setHighlightCanvasses] = useState(false);
    const [highlightInsights, setHighlightInsights] = useState(false);
    const profileRef = useRef(null);

    useEffect(() => {
        const start = () => setHighlightNewCanvass(true);
        const stop = () => setHighlightNewCanvass(false);
        window.addEventListener('start-highlight-new-canvass', start);
        window.addEventListener('stop-highlight-new-canvass', stop);
        return () => {
            window.removeEventListener('start-highlight-new-canvass', start);
            window.removeEventListener('stop-highlight-new-canvass', stop);
        };
    }, []);

    useEffect(() => {
        const start = () => setHighlightCanvasses(true);
        const stop = () => setHighlightCanvasses(false);
        window.addEventListener('start-highlight-canvasses', start);
        window.addEventListener('stop-highlight-canvasses', stop);
        return () => {
            window.removeEventListener('start-highlight-canvasses', start);
            window.removeEventListener('stop-highlight-canvasses', stop);
        };
    }, []);

    useEffect(() => {
        const start = () => setHighlightInsights(true);
        const stop = () => setHighlightInsights(false);
        window.addEventListener('start-highlight-insights', start);
        window.addEventListener('stop-highlight-insights', stop);
        return () => {
            window.removeEventListener('start-highlight-insights', start);
            window.removeEventListener('stop-highlight-insights', stop);
        };
    }, []);

    const handleCreateCanvass = () => {
        setFormError('');
        if (!canvassTitle.trim()) {
            setFormError('Please provide a title.');
            return;
        }

        const selectedAdminClient = Array.isArray(adminClients)
            ? adminClients.find((c) => (c?.clientId || c?.id) === selectedAdminClientId)
            : undefined;

        // Store the state in sessionStorage for the new page to pick up
        const newState = {
            title: canvassTitle.trim(),
            canvassBrief: canvassBrief.trim(),
            ...(selectedAdminClientId
                ? {
                    clientId: selectedAdminClientId,
                    displayName: selectedAdminClient?.displayName || null,
                  }
                : {}),
        };
        sessionStorage.setItem('newCanvassState', JSON.stringify(newState));

        // Force a full page reload to ensure proper state reset
        window.location.href = '/canvass/new';
    };

    const isAdmin = Boolean(account?.idTokenClaims?.roles?.includes('staging_administrator') || account?.idTokenClaims?.roles?.includes('production_administrator'));

    useEffect(() => {
        let cancelled = false;
        const loadClients = async () => {
            try {
                setAdminClientsLoading(true);
                setAdminClientsError('');
                const data = await getAllClients({ page: 1, pageSize: 200, isUser: true });
                const clients = Array.isArray(data?.clients) ? data.clients : [];
                if (!cancelled) {
                    setAdminClients(clients);
                }
            } catch (e) {
                if (!cancelled) setAdminClientsError(e?.message || 'Failed to load clients');
            } finally {
                if (!cancelled) setAdminClientsLoading(false);
            }
        };

        // if (showNewCanvassModal && isAdmin) {
        //     loadClients();
        // }
        return () => {
            cancelled = true;
        };
    }, [showNewCanvassModal, isAdmin]);

    const handleNewCanvassClick = () => {
        const created = getIsFirstCanvassCreated();
        if (created === true) {
            setShowNewCanvassModal(true);
        } else if (created === false || typeof created === 'undefined') {
            setShowTutorialModal(true);
        }
    };

    const handleTutorialOk = async () => {
        try {
            const oid = instance.getActiveAccount()?.idTokenClaims?.oid || account?.localAccountId;
            if (oid) {
                await updateFirstCanvassCreated({ clientId: oid });
                setIsFirstCanvassCreated(true);
            }
        } catch {}
        setShowTutorialModal(false);
        setShowNewCanvassModal(true);
    };

    return (
        <nav className={styles.navBar}>
            <a href="/" className={styles.navLogoContainer}>
                <Icon name="augustsays/logo" color="var(--nav-logo-color)" height="32" width="61"/>
            </a>
            
            {/* Mobile End Interview Button - Only show on interview pages */}
            {isInterview && canEndInterview && (
                <div className={styles.mobileEndInterviewButton}>
                    <Button
                        type="button"
                        variant="primary"
                        size="sm"
                        disabled={isDisabled}
                        onClick={() => {
                            // Dispatch custom event to notify GameAssistantChat
                            window.dispatchEvent(new CustomEvent('endInterviewRequested'));
                        }}
                    >
                        End Interview
                    </Button>
                </div>
            )}
            
            {!shouldHideNavItems && (
                <>
                    <div className={styles.navMenuContainer}>
                        {/* <Link to="/dashboard" className={`${styles.navMenuItem} ${location.pathname === '/' ? styles.isActive : ''}`}>
                            <div className={styles.navMenuContent}>
                                <div className={styles.navMenuIcon}>
                                    <Icon name="augustsays/home" color="var(--nav-icon-color)" height="24" width="25"/>
                                </div>
                                <div className={styles.navMenuText}>Dashboard</div>
                            </div>
                        </Link> */}
                        
                        <Link to="/canvasses" className={`${styles.navMenuItemFirst} ${styles.navMenuItem } ${highlightCanvasses ? styles.navMenuHighlight : ''} ${location.pathname === '/canvasses' ? styles.isActive : ''}`}>
                            <div className={styles.navMenuContent}>
                                <div className={styles.navMenuIcon}>
                                    <Icon name="augustsays/canvasses" color="var(--nav-icon-color)" height="24" width="25"/>
                                </div>
                                <div className={styles.navMenuText}>Canvasses</div>
                            </div>
                        </Link>

                        <Link to="/analytics" className={`${styles.navMenuItem} ${location.pathname === '/analytics' ? styles.isActive : ''}`}>
                            <div className={styles.navMenuContent}>
                                <div className={styles.navMenuIcon}>
                                    <Icon name="augustsays/home" color="var(--nav-icon-color)" height="24" width="25"/>
                                </div>
                                <div className={styles.navMenuText}>Dashboard</div>
                            </div>
                        </Link>

                        {/* <Link to="/Insights" className={`${styles.navMenuItem} ${highlightInsights ? styles.navMenuHighlight : ''} ${location.pathname === '/insights' ? styles.isActive : ''}`}>
                            <div className={styles.navMenuContent}>
                                <div className={styles.navMenuIcon}>
                                    <Icon name="augustsays/home" color="var(--nav-icon-color)" height="24" width="25"/>
                                </div>
                                <div className={styles.navMenuText}>Insights</div>
                            </div>
                        </Link> */}
                        
                        { Boolean(account?.idTokenClaims?.roles?.includes('staging_administrator') || account?.idTokenClaims?.roles?.includes('production_administrator')) && (
                            <Link to="/admin" className={`${styles.navMenuItem} ${location.pathname === '/admin' ? styles.isActive : ''}`}>
                                <div className={styles.navMenuContent}>
                                    <div className={styles.navMenuIcon}>
                                        <Icon name="augustsays/insights" color="var(--nav-icon-color)" height="24" width="25"/>
                                    </div>
                                    <div className={styles.navMenuText}>Admin</div>
                                </div>
                            </Link>
                        )}

                        <Link to="/welcome" className={`${styles.navMenuItem} ${location.pathname === '/welcome' ? styles.isActive : ''}`}>
                            <div className={styles.navMenuContent}>
                                <div className={styles.navMenuIcon}>
                                    <Icon name="augustsays/users" color="var(--nav-icon-color)" height="24" width="25"/>
                                </div>
                                <div className={styles.navMenuText}>Playbook</div>
                            </div>
                        </Link>
                    </div>
                    <div className={`${styles.navActionContainer} ${styles.desktopActions} ${highlightNewCanvass ? styles.navActionHighlight : ''}`}>
                        <Button textColor="var(--brand-as-neutral-950)" color="var(--brand-as-green-400)" size='lg'  onClick={handleNewCanvassClick}>
                            New Canvass
                        </Button>
                    </div>
                    {/* Right group: hamburger (first) + profile (second), aligned to right */}
                    <div className={`${styles.navRightGroup}`}>
                        <div className={`${styles.navActionContainer} ${styles.mobileActions}`}>
                            <button
                                type="button"
                                className={styles.hamburger}
                                aria-haspopup="menu"
                                aria-expanded={authMenuOpen ? 'true' : 'false'}
                                aria-controls="auth-mobile-menu"
                                onClick={() => setAuthMenuOpen(v => !v)}
                            >
                                <span className={styles.hamburgerBar} />
                                <span className={styles.hamburgerBar} />
                                <span className={styles.hamburgerBar} />
                            </button>
                            {authMenuOpen && (
                                <div id="auth-mobile-menu" role="menu" className={styles.mobileMenu}>
                                    <Link role="menuitem" to="/canvasses" className={styles.mobileMenuItem} onClick={() => setAuthMenuOpen(false)}>
                                        Canvasses
                                    </Link>
                                    <Link role="menuitem" to="/analytics" className={styles.mobileMenuItem} onClick={() => setAuthMenuOpen(false)}>
                                        Dashboard
                                    </Link>
                                    {/* <Link role="menuitem" to="/insights" className={styles.mobileMenuItem} onClick={() => setAuthMenuOpen(false)}>
                                        Insights
                                    </Link> */}
                                    { Boolean(account?.idTokenClaims?.roles?.includes('staging_administrator') || account?.idTokenClaims?.roles?.includes('production_administrator')) && (
                                        <Link role="menuitem" to="/admin" className={styles.mobileMenuItem} onClick={() => setAuthMenuOpen(false)}>
                                            Admin
                                        </Link>
                                    )}
                                    <Link role="menuitem" to="/welcome" className={styles.mobileMenuItem} onClick={() => setAuthMenuOpen(false)}>
                                        Playbook
                                    </Link>
                                    <button role="menuitem" type="button" className={styles.mobileMenuItem} onClick={() => { setAuthMenuOpen(false); handleNewCanvassClick(); }}>
                                        New Canvass
                                    </button>
                                    {!account && (
                                        <button role="menuitem" type="button" className={styles.mobileMenuItem} onClick={() => { setAuthMenuOpen(false); handleLoginRedirect(); }}>
                                            Sign In
                                        </button>
                                    )}
                                </div>
                            )}
                        </div>
                        {account && (
                            <div className={styles.navActionContainer}>
                                <div ref={profileRef} className={styles.profileContainer} style={{ position: 'relative' }}>
                                    <button
                                        type="button"
                                        className={styles.profileTrigger}
                                        onClick={() => setShowProfileMenu(v => !v)}
                                        aria-haspopup="menu"
                                        aria-expanded={showProfileMenu ? 'true' : 'false'}
                                        title={account.name || 'User'}
                                    >
                                        <div className={styles.profileAvatar}>
                                            {((account.name || '').trim().split(/\s+/).map(p => p[0]).slice(0,2).join('') || 'U').toUpperCase()}
                                        </div>
                                        <div className={styles.profileMeta}>
                                            <div className={styles.profileName}>{account.name || 'User'}</div>
                                            <div className={styles.profileEmail}>{account.username || ''}</div>
                                        </div>
                                    </button>

                                    {showProfileMenu && (
                                        <div
                                            role="menu"
                                            className={styles.profileMenu}
                                        >
                                            <button
                                                role="menuitem"
                                                type="button"
                                                onClick={() => { setShowProfileMenu(false); navigate('/profile'); }}
                                                className={styles.profileMenuItem}
                                            >
                                                View Profile
                                            </button>
                                            <div className={styles.profileMenuDivider} />
                                            <button
                                                role="menuitem"
                                                type="button"
                                                onClick={() => { setShowProfileMenu(false); handleLogoutRedirect(); }}
                                                className={styles.profileMenuItem}
                                            >
                                                Sign Out
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                    {showTutorialModal && (
                        <div className={styles.modalOverlay} role="dialog" aria-modal="true" aria-labelledby="tutorial-title">
                            <div className={styles.modal}>
                                <div className={styles.modalHeader}>
                                    <div id="tutorial-title" className={styles.modalTitle}>How to create a canvass</div>
                                    <div className={styles.modalSubtext}>Creating a Canvass</div>
                                </div>
                                <div className={styles.modalBody}>
                                    <p className={styles.inputSubLabel}>
                                        You’ll work with August to onboard your new canvass through a light interview that defines your main objective, sub-objectives, and target audience.
                                    </p>
                                    <p className={styles.inputSubLabel}>
                                        Once drafted, you can review and edit your canvass until it’s approved. After approval, no further changes can be made.
                                    </p>
                                    <p className={styles.inputSubLabel}>
                                        Next, you’ll move to the Voice experience — where you can choose from three generated introductions or write your own. You can preview the interview before activating it, but data collection begins only once your canvass is active.
                                    </p>
                                </div>
                                <div className={styles.modalFooter}>
                                    <Button className={styles.fullWidthButton} variant="primary" onClick={handleTutorialOk}>OK</Button>
                                    <Button className={styles.fullWidthButton} variant="outline-neutral" onClick={() => setShowTutorialModal(false)}>Cancel</Button>
                                </div>
                            </div>
                        </div>
                    )}

                    {showNewCanvassModal && (
                        <div className={styles.modalOverlay} role="dialog" aria-modal="true" aria-labelledby="new-canvass-title">
                            <div className={styles.modal}>
                                <div className={styles.modalHeader}>
                                    <div id="new-canvass-title" className={styles.modalTitle}>New Canvass</div>
                                    <div className={styles.modalSubtext}>Let's get started, first we just need some basic information for your canvass.</div>
                                </div>
                                {formError && <div className={styles.errorText + ' ' + styles.inputSubLabel}>{formError}</div>}
                                <div className={styles.modalBody}>
                                    <div className={styles.inputTextArea}>
                                        <label className={styles.inputLabel} htmlFor="canvass-title">Canvass name<div className={styles.inputSubLabel} htmlFor="canvass-title">(required)</div></label>
                                        <input
                                            id="canvass-title"
                                            className={styles.input}
                                            type="text"
                                            value={canvassTitle}
                                            onChange={(e) => setCanvassTitle(e.target.value)}
                                            placeholder="Enter a title"
                                        />
                                    </div>

                                    <div className={styles.inputTextArea}>
                                        <label className={styles.inputLabel} htmlFor="canvass-brief">Canvass brief<div className={styles.inputSubLabel + ' ' + styles.charCount}>{`${canvassBrief.length}/240`}</div></label>
                                        <textarea
                                                id="canvass-brief"
                                                className={styles.textarea + ' ' + styles.canvassDescription}
                                                value={canvassBrief}
                                                onChange={(e) => setCanvassBrief(e.target.value.slice(0, 240))}
                                            placeholder="Add context and purpose for this canvass (max 240 characters)"
                                            maxLength={240}
                                            rows={4}
                                        />
                                    </div>

                                    {/* {isAdmin && (
                                        <div className={styles.inputTextArea}>
                                            <label className={styles.inputLabel} htmlFor="admin-client-select">
                                                Client
                                                <div className={styles.inputSubLabel}>(admin)</div>
                                            </label>
                                            <select
                                                id="admin-client-select"
                                                className={styles.input}
                                                value={selectedAdminClientId}
                                                onChange={(e) => setSelectedAdminClientId(e.target.value)}
                                                disabled={adminClientsLoading}
                                            >
                                                <option value="">Select a client</option>
                                                {adminClients.map((c) => (
                                                    <option key={c.clientId || c.id} value={c.clientId}>
                                                        {c.displayName || c.clientId}
                                                    </option>
                                                ))}
                                            </select>
                                            {adminClientsError && (
                                                <div className={styles.errorText + ' ' + styles.inputSubLabel}>{adminClientsError}</div>
                                            )}
                                        </div>
                                    )} */}
                                </div>
                                <div className={styles.modalFooter}>
                                    <Button className={styles.fullWidthButton} variant="primary" onClick={handleCreateCanvass}>Create Canvass</Button>
                                    <Button className={styles.fullWidthButton} variant="outline-neutral" onClick={() => { setFormError(''); setShowNewCanvassModal(false); setSelectedAdminClientId(''); }}>Cancel</Button>
                                </div>
                            </div>
                        </div>
                    )}
                </>
            )}
        </nav>
    );
};

// Main NavigationBar component that switches based on auth mode
export const NavigationBar = () => {
    const { instance } = useMsal();
    const account = useAccount(instance.getActiveAccount());
    const location = useLocation();
    if (!account && location.pathname === '/') {
        return null;
    }
    return account ? <AuthNavigationBar /> : <NonAuthNavigationBar />;
};
